import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
} from '@angular/core';
import {
  LocalizedProcess,
  ProcessActionNotificationType,
} from '../../../models/process-models/index';
import { saveAs } from 'file-saver';
import { ProcessStage } from '../../../models/process-models/process-stage.enum';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ProcessService } from '../../../services/process.service';
import { ConfigService } from '../../../services/config.service';
import { HttpParams } from '@angular/common/http';
import { ReportingService } from '../../../services/reporting.service';
import { DeadlineChangeModalComponent } from '../../../common/modal/deadline-change-modal/deadline-change-modal.component';
import { first } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-process-list-action-menu',
  templateUrl: './process-list-action-menu.component.html',
  styleUrls: ['./process-list-action-menu.component.scss'],
})
export class ProcessListActionMenuComponent {
  @Input()
  processes: Array<LocalizedProcess>;

  @Output()
  onChangesMade = new EventEmitter<any>();

  modalRef: BsModalRef;
  appraiseeBlockVisible: boolean;
  respondentBlockVisible: boolean;
  pmBlockVisible: boolean;
  hrBlockVisible: boolean;
  hrBlockHide = true;
  notificationType = ProcessActionNotificationType;
  selectedNotificationType: ProcessActionNotificationType;

  // TODO: implement spinner toggle
  isSpinnerEnable = false;

  constructor(
    private readonly bsModalService: BsModalService,
    private processService: ProcessService,
    private readonly configService: ConfigService,
    private readonly reportingService: ReportingService,
    private readonly toastr: ToastrService
  ) {}

  openModal(
    modalTemplate: TemplateRef<any>,
    notificationType: ProcessActionNotificationType
  ) {
    this.modalRef = this.bsModalService.show(modalTemplate);
    this.selectedNotificationType = notificationType;
  }

  hideModal() {
    if (!this.modalRef) {
      return;
    }
    this.modalRef.hide();
  }

  sendToAll() {
    this.processService
      .notifyAll(this.selectedNotificationType)
      .subscribe(() => this.hideModal());
  }

  sendToSelected() {
    const selectedProcesses = this.getSelectedProcesses();
    if (selectedProcesses.length === 0) {
      return;
    }
    this.processService
      .notifySelected(this.selectedNotificationType, selectedProcesses)
      .subscribe(() => this.hideModal());
  }

  getIsCancelBtnEnabled() {
    return this.processes.some(
      (p) => p.isSelected && p.stage !== ProcessStage.Completed
    );
  }

  duplicate() {
    this.processService
      .duplicate(this.getSelectedProcesses())
      .subscribe(() => this.onChangesMade.next());
  }

  sendHrSummary() {
    this.toastr.info("Notifications sending started");
    this.processService.sendHrSummaryNotification(this.getSelectedProcesses().map(p => p.id))
    .subscribe(() => this.toastr.success("Notifications were sent successfully"), error => this.toastr.error("An error while sending the notifications"));
  }

  downloadProcessesInfo() {
    const httpParams = this.createSelectedDurationHttpParams();
    this.processService
      .getSelectedProcessInfo(httpParams)
      .subscribe((data) =>
        saveAs(data, `Processes_info_${new Date().toISOString()}.xlsx`)
      );
  }

  downloadExcelReport() {
    this.reportingService
      .getCrossprocessSummaryExcelReport(
        this.getSelectedProcesses().map((p) => p.id)
      )
      .subscribe((r) => saveAs(r, `Summary report.xlsx`));
  }

  async onChangeDeadlineClicked() {
    this.modalRef = this.bsModalService.show(DeadlineChangeModalComponent, {
      initialState: { minDate: new Date() },
    } as ModalOptions);
    this.bsModalService.onHidden.pipe(first()).subscribe(async () => {
      if (!this.modalRef.content.isSaveClicked) return;
      let activeIds = this.getSelectedProcesses()
        .filter((c) => c.stage == ProcessStage.Active)
        .map((c) => c.durationId);
      await this.processService
        .changeDeadline(this.modalRef.content.date, activeIds)
        .toPromise();
      this.onChangesMade.emit();
    });
  }

  get isSelectedProcesses(): boolean {
    return this.processes.some((p) => p.isSelected);
  }

  get isSelectedActive(): boolean {
    return (
      this.isSelectedProcesses &&
      this.getSelectedProcesses().every((p) => p.stage == ProcessStage.Active)
    );
  }

  get isOfTheSameSurvey(): boolean {
    return (
      this.getSelectedProcesses().reduce((acc, p) => {
        if (acc.indexOf(p.surveyId) === -1) {
          acc.push(p.surveyId);
        }

        return acc;
      }, [] as Array<number>).length === 1
    );
  }

  private createSelectedDurationHttpParams(): HttpParams {
    const selectedProcesses = this.getSelectedProcesses();
    if (!selectedProcesses) {
      return;
    }
    return this.configService.appendHttpParams(
      new HttpParams(),
      'durationIds',
      selectedProcesses.map((sp) => sp.durationId)
    );
  }

  private getSelectedProcesses(): Array<LocalizedProcess> {
    return this.processes.filter((c) => c.isSelected);
  }
}
